import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Mail, LifeBuoy, ShieldCheck, Scale } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import '../styles/site-pages.css';

/**
 * /contact — contact information (issue #263).
 *
 * Deliberately NOT a contact form. A form needs an endpoint, spam protection
 * and a mailbox someone actually watches; a wrong or unattended one is worse
 * than a plain address for an Apple review, which is checking that a real
 * business is reachable. Every address below is one the codebase already
 * routes mail to, and info@ is the grievance contact named in our own
 * Privacy Policy — so this page agrees with the legal text rather than
 * introducing a second, competing answer.
 */
const CHANNELS = [
  {
    icon: Mail,
    title: 'General enquiries',
    email: 'info@teachgenie.ai',
    note: 'Partnerships, press, institutions, and anything that does not fit below.',
  },
  {
    icon: LifeBuoy,
    title: 'Product support',
    email: 'support@teachgenie.ai',
    note: 'Trouble with your account, generation, billing or the desktop client.',
  },
  {
    icon: ShieldCheck,
    title: 'MARS & assessments',
    email: 'mars@teachgenie.ai',
    note: 'Proctoring, exam sessions, results and integrity reports.',
  },
  {
    icon: Scale,
    title: 'Privacy & grievance',
    email: 'info@teachgenie.ai',
    note: 'Data requests and grievances under the DPDP Act, 2023.',
  },
];

export default function ContactPage() {
  return (
    <div className="site-page">
      <Helmet>
        <title>Contact — TeachGenie™.ai</title>
        <meta
          name="description"
          content="Contact TeachGenie™.ai — general enquiries, product support, MARS assessments, and privacy or grievance matters. Operated by YOM Private Limited, India."
        />
        <link rel="canonical" href="https://www.teachgenie.ai/contact" />
      </Helmet>

      <Navbar />

      <main className="site-page-inner">
        <span className="site-page-kicker">Contact</span>
        <h1>Talk to us.</h1>
        <p className="site-page-lede">
          Every address below reaches a person. Pick whichever fits — if you are not sure,
          use <a href="mailto:info@teachgenie.ai">info@teachgenie.ai</a> and we will route it.
        </p>

        <section className="site-section">
          <div className="site-cards">
            {CHANNELS.map(({ icon: Icon, title, email, note }) => (
              <div className="site-card" key={title}>
                <div className="site-card-icon"><Icon size={20} aria-hidden="true" /></div>
                <h3>{title}</h3>
                <p style={{ marginBottom: 8 }}>{note}</p>
                <a href={`mailto:${email}`}>{email}</a>
              </div>
            ))}
          </div>
        </section>

        <section className="site-section">
          <h2>Company details</h2>
          <dl className="site-rows">
            <div className="site-row">
              <dt>Legal entity</dt>
              <dd>
                YOM Private Limited
                <span className="sub">DPIIT-recognised startup, incorporated in India</span>
              </dd>
            </div>
            <div className="site-row">
              <dt>Product</dt>
              <dd>
                TeachGenie™.ai
                <span className="sub">Built and managed by Vertos of LPU</span>
              </dd>
            </div>
            <div className="site-row">
              <dt>Governing law</dt>
              <dd>
                Laws of India
                <span className="sub">
                  Courts at New Delhi have exclusive jurisdiction, as set out in our{' '}
                  <Link to="/terms">Terms of Service</Link>
                </span>
              </dd>
            </div>
            <div className="site-row">
              <dt>Grievance contact</dt>
              <dd>
                <a href="mailto:info@teachgenie.ai">info@teachgenie.ai</a>
                <span className="sub">
                  Named as the privacy and grievance contact in our{' '}
                  <Link to="/privacy">Privacy Policy</Link>
                </span>
              </dd>
            </div>
          </dl>
        </section>

        <section className="site-section">
          <p className="site-callout">
            Looking for something specific? The <Link to="/sitemap">sitemap</Link> lists every
            public page, and <Link to="/policies">policies</Link> covers terms, privacy,
            billing, cookies and disclaimers.
          </p>
        </section>
      </main>

      <Footer />
    </div>
  );
}
